import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useForm } from 'react-hook-form';
import Alert from 'react-bootstrap/lib/Alert';
import { useT } from 'client/i18n/hooks/useT';
import { Button } from 'shared/Button/NewButton';
import { Footer } from '../Footer';
import { FieldInput } from '../FieldInput';

const Form = styled.form`
  width: 100%;
  max-width: 362px;
  padding: 0px 20px;

  button {
    width: 100%;
  }
`;

const InputWrapper = styled.div`
  > * {
    margin-bottom: 15px;
  }
`;

const Container = styled.div`
  display: flex;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  font-size: 1.25rem;
  background: white;
  p {
    color: #292f36;
    text-align: center;
  }
`;

type ResetPasswordData = {
  password: string;
  repeatPassword: string;
};

type ResetPasswordProps = {
  onSubmit: (data: ResetPasswordData) => Promise<void>;
  resetTokenIsValid: boolean;
  resetWasSuccessful: boolean;
};

export function ResetPassword({
  onSubmit,
  resetTokenIsValid,
  resetWasSuccessful,
}: ResetPasswordProps) {
  const t = useT();
  const {
    handleSubmit,
    register,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm();

  const [formError, setFormError] = useState('');

  const onValid = (data: ResetPasswordData) => {
    setFormError('');
    onSubmit(data).catch(({ message }) => {
      setFormError(message);
    });
  };

  return (
    <>
      <Container>
        {!resetTokenIsValid && (
          <p>
            {t(
              'ResetPassword.ResetLinkInvalid',
              'Sorry, but that reset password link is no longer valid.',
              'Reset password link expired error message'
            )}
          </p>
        )}
        {resetWasSuccessful && (
          <>
            <p>
              {t(
                'ResetPassword.ResetSuccessMessage',
                'Your password has been saved!',
                'Reset password success message'
              )}
            </p>
            <p>
              {t(
                'ResetPassword.LoginAfterResetMessage',
                'You can now log in with it.',
                'Login after reset password message'
              )}
            </p>
          </>
        )}
        {resetTokenIsValid && !resetWasSuccessful && (
          <Form
            onSubmit={handleSubmit(onValid)}
            method="POST"
            noValidate={true}
          >
            {formError && <Alert bsStyle="danger">{formError}</Alert>}
            <InputWrapper>
              <FieldInput
                {...register('password', {
                  validate: pw =>
                    pw.length >= 7 ||
                    t(
                      'ResetPassword.PasswordFieldInvalid',
                      'Passwords must be 7 characters or more',
                      'Reset password field invalid message'
                    ),
                })}
                placeholder={t(
                  'ResetPassword.PasswordFieldText',
                  'Password',
                  'Password field text'
                )}
                type="password"
                error={errors.password}
              />
              <FieldInput
                {...register('repeatPassword', {
                  validate: pw =>
                    pw === getValues('password') ||
                    t(
                      'ResetPassword.RepeatPasswordFieldInvalid',
                      'The passwords do not match',
                      'Repeat password invalid message'
                    ),
                })}
                placeholder={t(
                  'ResetPassword.RepeatPasswordFieldPlaceholder',
                  'Type it again',
                  'Repeat password field placeholder'
                )}
                type="password"
                error={errors.repeatPassword}
              />
            </InputWrapper>
            <Button type="submit" isDisabled={isSubmitting} color="purple">
              {t(
                'ResetPassword.ResetPasswordButtonLabel',
                'Reset Password',
                'Reset password button text'
              )}
            </Button>
          </Form>
        )}
      </Container>
      <Footer />
    </>
  );
}
